import * as React from 'react';
import type { AiPlatformService, ModelRegistryInfo } from './AiPlatformService';

export interface AiPlatformContextValue {
  service: AiPlatformService;
  tenantSlug: string;
  appSlug: string;
  onAgentClick?: (agentId: string) => void;
  onReleaseClick?: (agentId: string, releaseId: string) => void;
  onPlaygroundClick?: (agentId: string, agentName: string, releaseId: string) => void;
  onModelClick?: (modelId: string) => void;
  modelRegistry?: ModelRegistryInfo[];
  /** providerModel → custom label (from the tenant alias system) */
  modelAliasLabels?: Record<string, string>;
}

const AiPlatformContext = React.createContext<AiPlatformContextValue | null>(null);

export interface AiPlatformProviderProps {
  service: AiPlatformService;
  tenantSlug: string;
  appSlug: string;
  children: React.ReactNode;
  onAgentClick?: (agentId: string) => void;
  onReleaseClick?: (agentId: string, releaseId: string) => void;
  onPlaygroundClick?: (agentId: string, agentName: string, releaseId: string) => void;
  onModelClick?: (modelId: string) => void;
  modelRegistry?: ModelRegistryInfo[];
  modelAliasLabels?: Record<string, string>;
}

export function AiPlatformProvider({
  service,
  tenantSlug,
  appSlug,
  children,
  onAgentClick,
  onReleaseClick,
  onPlaygroundClick,
  onModelClick,
  modelRegistry,
  modelAliasLabels,
}: AiPlatformProviderProps) {
  const value = React.useMemo(
    () => ({
      service,
      tenantSlug,
      appSlug,
      onAgentClick,
      onReleaseClick,
      onPlaygroundClick,
      onModelClick,
      modelRegistry,
      modelAliasLabels,
    }),
    [
      service,
      tenantSlug,
      appSlug,
      onAgentClick,
      onReleaseClick,
      onPlaygroundClick,
      onModelClick,
      modelRegistry,
      modelAliasLabels,
    ],
  );
  return <AiPlatformContext.Provider value={value}>{children}</AiPlatformContext.Provider>;
}

export function useAiPlatform(): AiPlatformContextValue {
  const ctx = React.useContext(AiPlatformContext);
  if (!ctx) throw new Error('useAiPlatform must be used inside <AiPlatformProvider>');
  return ctx;
}
